/**
 * Model Pricing — SINGLE SOURCE OF TRUTH
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every model's real Runware API cost (USD) lives here, transcribed from the
 * per-model reference docs in `server/docs/<provider>/<model>.md` (cross-checked
 * against `Runware-pricing.md`). Each value cites its source.
 *
 * Credits charged to the user are ALWAYS derived from this file via
 * `creditsFromApiCost()` — never hand-set per flow. The DB `credit_costs`
 * seed (server/scripts/seed-credit-costs.mjs), the consolidating migration,
 * and the frontend reference (src/config/pricing.ts) are all generated from
 * this module. Adding a model = add one entry here.
 *
 * Imported by both server (relative) and frontend (`@shared/pricing/modelPricing`).
 *
 * ─── Pricing rule ──────────────────────────────────────────────────────────
 * For each variant, the USD value is the MOST EXPENSIVE configuration the flow
 * can produce at that tier (e.g. OpenAI 4k can be square OR portrait/landscape
 * — we store the dearer one) so realized margin never silently drops below the
 * target. credits = ceil(apiCost × 100). At the Starter rate ($0.02/credit)
 * that is ~50% gross margin. Raising the floor later = change the ONE function
 * `creditsFromApiCost` (e.g. multiply by 1.2) — never per-flow edits.
 *
 * ⚠ Margin is plan-dependent: ~50% holds at the Starter $0.02/credit rate.
 * Pro/Creator/annual/coupon/credit-pack rates give more credits per dollar,
 * so realized margin is lower on discounted plans. The formula targets the
 * Starter rate by design; revisit `creditsFromApiCost` to change that stance.
 */

/** 1 credit = $0.02 to the user (Starter plan rate). Mirror of pricing.ts. */
export const CREDIT_VALUE_USD = 0.02;

/** The ONE pricing formula. ceil so margin is always ≥ the target (50% @ $0.02). */
export function creditsFromApiCost(usd: number): number {
  return Math.ceil(usd * 100);
}

export interface ModelPricing {
  /** Image: resolution tier ("1k"|"2k"|"4k") → worst-case USD over every
   *  aspect ratio / quality the model can be asked for at that tier. */
  image?: Record<string, number>;
  /** Video: "<res>_<dur>s" and (for sound-toggle models) "<res>_<dur>s_audio"
   *  → USD. Models with `alwaysIncludesAudio` use the no-suffix key priced at
   *  the audio cost (the UI never sends an audio flag for them). */
  video?: Record<string, number>;
  /** Optional per-input/reference-image surcharge (USD per reference) that
   *  Runware bills ON TOP of the base. Documented for transparency and for the
   *  dynamic-surcharge follow-up; the canonical seed uses the base only.
   *  See [[per-reference-surcharge-followup]]. */
  perReferenceUsd?: number;
  /** Where the numbers came from (auditability). */
  source: string;
  /** Set when the doc has no authoritative price list and the value is a
   *  conservative worst-case from response examples. Surfaced by tests. */
  estimated?: boolean;
}

/**
 * Keyed by registry model id. MUST cover every enabled model's
 * supportedResolutions / supportedDurations (enforced by the boot assertion
 * in registry.ts and by modelPricing.test.ts — missing pricing fails loud).
 */
export const MODEL_API_COST_USD: Record<string, ModelPricing> = {
  // ── Image ────────────────────────────────────────────────────────────────

  // google-nano-banana-2.md: 1K $0.06895 / 2K $0.10255 / 4K $0.15295.
  // Also bills +$0.00028 per input image, +$0.14 grounded search (out of scope).
  "nano-banana-2": {
    image: { "1k": 0.06895, "2k": 0.10255, "4k": 0.15295 },
    perReferenceUsd: 0.00028,
    source: "server/docs/google/google-nano-banana-2.md",
  },

  // google:4@2 == Nano Banana PRO (NOT plain Nano Banana).
  // google-nano-banana-pro.md: 1K $0.138 / 2K $0.138 / 4K $0.244.
  "gemini-3-pro": {
    image: { "1k": 0.138, "2k": 0.138, "4k": 0.244 },
    source: "server/docs/google/google-nano-banana-pro.md",
  },

  // openai-gpt-image-1-5.md: cost = quality tier × aspect. Our resolution maps
  // to quality via OPENAI_RESOLUTION_PAYLOAD (1k→low, 2k→medium, 4k→high).
  // Worst aspect per tier: low $0.013, medium $0.051, high $0.20.
  // (+ small input-token cost on top → perReferenceUsd, conservative.)
  "gpt-image-1-5": {
    image: { "1k": 0.013, "2k": 0.051, "4k": 0.2 },
    perReferenceUsd: 0.03,
    source: "server/docs/openai/openai-gpt-image-1-5.md",
  },

  // openai-gpt-image-1.md has NO price table — only response examples, all at
  // quality high, costing $0.25. Flat worst-case ($0.25) at every tier
  // guarantees ≥50% margin (low/medium cost ≤ high). Verify via includeCost.
  "gpt-image-1": {
    image: { "1k": 0.25, "2k": 0.25, "4k": 0.25 },
    perReferenceUsd: 0.03,
    source: "server/docs/openai/openai-gpt-image-1.md (response examples; no price table)",
    estimated: true,
  },

  // openai-gpt-image-2.md: headline "$0.006" is only the base output-token
  // unit. Realized billed cost (response examples, quality high, 1024–1536px)
  // is $0.151–$0.165. Low/medium tier costs are NOT documented, so we charge
  // the documented worst realized cost ($0.16538) flat at every tier — never
  // loses money; verify low/medium via a real includeCost probe.
  "gpt-image-2": {
    image: { "1k": 0.16538, "2k": 0.16538, "4k": 0.16538 },
    perReferenceUsd: 0.03,
    source: "server/docs/openai/openai-gpt-image-2.md (response examples; no per-tier table)",
    estimated: true,
  },

  // bfl-flux-2-klein-9b.md: 1024×1024 $0.00078.
  "flux-klein-9b": {
    image: { "1k": 0.00078 },
    source: "server/docs/black-forest-labs-flux/bfl-flux-2-klein-9b.md",
  },

  // bfl-flux-1-kontext-pro.md: 1024×1024 $0.04 (1k square only).
  "flux-kontext-pro": {
    image: { "1k": 0.04 },
    source: "server/docs/black-forest-labs-flux/bfl-flux-1-kontext-pro.md",
  },

  // bfl-flux-2-max.md: $0.07 first megapixel, +$0.03/additional MP.
  // 1k worst (1024² ≈ 1.05MP) ≈ $0.07. 2k worst (1:1 2048² ≈ 4.19MP) ≈ $0.166.
  // Reference images +$0.03/MP → conservative perReferenceUsd.
  "flux-2-max": {
    image: { "1k": 0.07, "2k": 0.1658 },
    perReferenceUsd: 0.06,
    source: "server/docs/black-forest-labs-flux/bfl-flux-2-max.md",
  },

  // ── Video ────────────────────────────────────────────────────────────────

  // klingai-2-5-turbo-pro.md: $0.07/s for 1080p (1080p·5s = $0.35). 720p price
  // not published → charge the 1080p per-second rate for both (never lose).
  "kling-v2-5-turbo": {
    video: {
      "720p_5s": 0.35,
      "720p_10s": 0.7,
      "1080p_5s": 0.35,
      "1080p_10s": 0.7,
    },
    source: "server/docs/kling-ai/klingai-2-5-turbo-pro.md",
  },

  // klingai-video-2-6-pro.md (Runware-pricing.md "Kling VIDEO 2.6 Pro"):
  // 5s $0.35 / 10s $0.70 ; with audio 5s $0.70 / 10s $1.40. 1080p only.
  "kling-v2-6": {
    video: {
      "1080p_5s": 0.35,
      "1080p_10s": 0.7,
      "1080p_5s_audio": 0.7,
      "1080p_10s_audio": 1.4,
    },
    source: "server/docs/kling-ai/klingai-video-2-6-pro.md",
  },

  // klingai-video-3-0-pro.md: 1080p 1s = $0.168 (always includes audio).
  // 5s = $0.84, 10s = $1.68. No _audio suffix (model has supportsSound:false).
  "kling-v3-0-pro": {
    video: { "1080p_5s": 0.84, "1080p_10s": 1.68 },
    source: "server/docs/kling-ai/klingai-video-3-0-pro.md",
  },

  // klingai-video-3-0-standard.md: 1s no-audio $0.084 / audio $0.126.
  // 5s 0.42/0.63 ; 10s 0.84/1.26. Audio optional (supportsSound:true).
  "kling-v3-0-standard": {
    video: {
      "1080p_5s": 0.42,
      "1080p_10s": 0.84,
      "1080p_5s_audio": 0.63,
      "1080p_10s_audio": 1.26,
    },
    source: "server/docs/kling-ai/klingai-video-3-0-standard.md",
  },

  // openai-sora-2.md: $0.10/s for 720p (always includes audio). 720p only.
  "sora-2": {
    video: { "720p_4s": 0.4, "720p_8s": 0.8, "720p_12s": 1.2 },
    source: "server/docs/openai/openai-sora-2.md",
  },

  // openai-sora-2-pro.md: $0.30/s 720p, $0.50/s 1080p (always includes audio).
  "sora-2-pro": {
    video: {
      "720p_4s": 1.2,
      "720p_8s": 2.4,
      "720p_12s": 3.6,
      "1080p_4s": 2.0,
      "1080p_8s": 4.0,
      "1080p_12s": 6.0,
    },
    source: "server/docs/openai/openai-sora-2-pro.md",
  },

  // ── Dev-only cheap models (config/devModels.ts; hidden from prod) ──────────
  // Never charged in production (the override is hard-gated off there), so a
  // single flat worst-case-cheap price per variant is intentional — not a
  // margin concern. Priced flat across ALL tiers/variants the app can request
  // so a transparent dev swap's credit lookup always resolves (No Fallbacks).

  // bfl-flux-2-klein-9b.md: 1024×1024 $0.00078. Flat at every tier (the dev
  // model never upscales — see DEV_CHEAP_IMAGE_SIZE_MAP).
  "dev-cheap-image": {
    image: { "1k": 0.00078, "2k": 0.00078, "4k": 0.00078 },
    source: "server/docs/black-forest-labs-flux/bfl-flux-2-klein-9b.md",
  },

  // bytedance-seedance-1-0-pro-fast.md / Runware-pricing.md: cheapest config
  // (480p · 5s · 16:9) = $0.0315. Flat across every res_dur the app may send
  // (the dev model always renders at 480p regardless of requested tier).
  "dev-cheap-video": {
    video: {
      "720p_4s": 0.0315,
      "720p_5s": 0.0315,
      "720p_8s": 0.0315,
      "720p_10s": 0.0315,
      "720p_12s": 0.0315,
      "1080p_4s": 0.0315,
      "1080p_5s": 0.0315,
      "1080p_8s": 0.0315,
      "1080p_10s": 0.0315,
      "1080p_12s": 0.0315,
    },
    source: "server/docs/bytedance/bytedance-seedance-1-0-pro-fast.md (Runware-pricing.md)",
  },
};

/**
 * Talking Avatar pipeline — models NOT in MODEL_REGISTRY (pipeline-internal),
 * still centralized here so nothing is hand-priced per flow.
 *
 * - Preview pair is priced from the CHOSEN image model's canonical "1k" cost
 *   above (× TALKING_AVATAR_PREVIEW_COUNT) — no bespoke variant.
 * - Avatar render is per-second, formula-derived from the Runware per-second
 *   API cost (Runware-pricing.md "KlingAI Avatar 2.0 Standard/Pro").
 * - TTS + voice-clone have NO Runware doc for elevenlabs:27@1, so they stay
 *   explicit product-fixed credit prices (single source, not formula-derived).
 */
export const TALKING_AVATAR_PRICING = {
  /** Runware-pricing.md: KlingAI Avatar 2.0 Standard $0.044/s, Pro $0.087/s. */
  avatarPerSecUsd: { standard: 0.044, pro: 0.087 } as Record<string, number>,
  /** Product-fixed (no md for elevenlabs:27@1). Credits, not USD. */
  ttsCredits: 10,
  cloneVoiceCredits: 20,
  source:
    "Runware-pricing.md (KlingAI Avatar 2.0); TTS/clone product-fixed (no doc for elevenlabs:27@1)",
} as const;

/** Per-second avatar credits, formula-derived. standard→5/s, pro→9/s. */
export function avatarCreditsPerSec(tier: "standard" | "pro"): number {
  return creditsFromApiCost(TALKING_AVATAR_PRICING.avatarPerSecUsd[tier]);
}

/** Look up a model's pricing. Throws (No Fallbacks Policy) if absent. */
export function getModelPricing(modelId: string): ModelPricing {
  const p = MODEL_API_COST_USD[modelId];
  if (!p) {
    throw new Error(
      `No pricing for model "${modelId}" in modelPricing.ts — every enabled model must be priced`,
    );
  }
  return p;
}

/**
 * Credits for a model + media kind + variant. `refCount` adds the documented
 * per-reference surcharge before the single ceil(). Throws if the model or
 * variant is unpriced — a missing price must fail loud, never default.
 */
export function creditsForModelVariant(
  modelId: string,
  kind: "image" | "video",
  variant: string,
  refCount = 0,
): number {
  const pricing = getModelPricing(modelId);
  const table = kind === "image" ? pricing.image : pricing.video;
  const base = table?.[variant];
  if (base === undefined) {
    throw new Error(
      `No ${kind} price for "${modelId}" variant "${variant}" in modelPricing.ts`,
    );
  }
  const surcharge = (pricing.perReferenceUsd ?? 0) * Math.max(0, refCount);
  return creditsFromApiCost(base + surcharge);
}
